/* eslint-disable react/prop-types */
import { useState, useRef } from 'react';
// @mui
import { CircularProgress } from '@mui/material';
// ui
import { Button } from 'src/@/components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from 'src/@/components/ui/dialog';
import { Input } from 'src/@/components/ui/input';
import { Switch } from 'src/@/components/ui/switch';
import { Textarea } from 'src/@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from 'src/@/components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from 'src/@/components/ui/tabs';
import { Badge } from 'src/@/components/ui/badge';
// components
import Iconify from 'src/components/Iconify';
import ModalCategorias from './ModalCategorias';
// hooks / services / actions
import MicRecorder from 'mic-recorder-to-mp3';
import v2_actionUploadS3 from 'src/actions/v2/global/actionUploadS3';

// ----------------------------------------------------------------------

// Design System Classes
const LABEL_CLASSES = 'block text-xs font-semibold text-gray-500 uppercase tracking-wider mb-2';
const INPUT_CLASSES = 'rounded-xl bg-[#F9FAFB] border-gray-200 focus:border-[#7C3AED] focus:ring-0 transition-all';
const SECTION_TITLE = 'text-lg font-semibold text-gray-900 mb-4';
const CARD_CLASSES = 'p-6 bg-white rounded-xl border border-gray-100 shadow-sm';

// ----------------------------------------------------------------------

export default function ModalProdutos({
    open,
    onClose,
    onSubmit,
    onEdit,
    isEdit,
    dataProduct,
    // Change handlers
    changeName,
    changeSku,
    changeRef,
    changeDescription,
    changePrice,
    changePriceSale,
    changeSale,
    changeStock,
    changeSequence,
    changeCategory,
    changeImage,
    changeTags,
    changeAttributes,
    changeLink,
    // Context
    user,
    cliente,
    // Categories
    categories = [],
    categoryActions,
}) {
    const [recorder] = useState(new MicRecorder({ bitRate: 128 }));
    const [isRecording, setIsRecording] = useState(false);
    const [recordingTime, setRecordingTime] = useState(0);
    const [uploading, setUploading] = useState(false);
    const [uploadingImage, setUploadingImage] = useState(false);
    const [openCategories, setOpenCategories] = useState(false);
    const [tagInput, setTagInput] = useState('');
    const [activeTab, setActiveTab] = useState('info');

    const fileInputRef = useRef(null);
    const imageInputRef = useRef(null);
    const timerRef = useRef(null);

    // Sequence helper function
    const updateSequence = (newSequence) => {
        if (changeSequence) changeSequence(newSequence);
    };

    // --- Image Handling ---
    const handleImageUpload = async (e) => {
        const file = e.target.files[0];
        if (!file) return;

        setUploadingImage(true);
        try {
            const formData = new FormData();
            formData.append('file', file);
            formData.append('filename', file.name);

            const querys = `?path=plataforma/produtos/${cliente?._id || 'temp'}`;
            const response = await v2_actionUploadS3(formData, querys);

            if (response?.data?.data) {
                changeImage(response.data.data.location);
            }
        } catch (error) {
            console.error('Erro ao fazer upload da imagem:', error);
        } finally {
            setUploadingImage(false);
            if (imageInputRef.current) imageInputRef.current.value = '';
        }
    };

    // --- Tags Handling ---
    const handleAddTag = () => {
        if (!tagInput.trim()) return;
        const currentTags = dataProduct?.tags || [];
        if (!currentTags.includes(tagInput.trim())) {
            changeTags([...currentTags, tagInput.trim()]);
        }
        setTagInput('');
    };

    const handleRemoveTag = (tag) => {
        const currentTags = dataProduct?.tags || [];
        changeTags(currentTags.filter((t) => t !== tag));
    };

    // --- Step Handling ---
    const addTextStep = () => {
        const newStep = { id: Date.now().toString(), type: 'text', content: '' };
        updateSequence([...(dataProduct?.sequence || []), newStep]);
    };

    const handleFileUpload = async (e) => {
        const files = Array.from(e.target.files);
        if (files.length === 0) return;

        setUploading(true);
        try {
            const uploadedFiles = [];
            for (const file of files) {
                const formData = new FormData();
                formData.append('file', file);
                formData.append('filename', file.name);

                const querys = `?path=plataforma/produtos/${cliente?._id || 'temp'}`;
                const response = await v2_actionUploadS3(formData, querys);

                if (response?.data?.data) {
                    uploadedFiles.push({
                        id: Date.now().toString() + Math.random(),
                        type: 'file',
                        content: response.data.data.location,
                        name: file.name,
                        key: response.data.data.key,
                    });
                }
            }
            updateSequence([...(dataProduct?.sequence || []), ...uploadedFiles]);
        } catch (error) {
            console.error('Erro ao fazer upload:', error);
        } finally {
            setUploading(false);
            if (fileInputRef.current) fileInputRef.current.value = '';
        }
    };

    const startRecording = async () => {
        try {
            await navigator.mediaDevices.getUserMedia({ audio: true });
            await recorder.start();
            setIsRecording(true);
            setRecordingTime(0);
            timerRef.current = setInterval(() => {
                setRecordingTime((prev) => prev + 1);
            }, 1000);
        } catch (err) {
            console.error('Erro ao iniciar gravação:', err);
        }
    };

    const stopRecording = async () => {
        if (timerRef.current) clearInterval(timerRef.current);
        setIsRecording(false);
        setUploading(true);
        try {
            const [buffer, blob] = await recorder.stop().getMp3();
            const file = new File(buffer, 'audio.mp3', { type: blob.type, lastModified: Date.now() });

            const formData = new FormData();
            formData.append('file', file);

            const querys = `?path=plataforma/produtos/${cliente?._id || 'temp'}`;
            const response = await v2_actionUploadS3(formData, querys);

            if (response?.data?.data) {
                const newStep = {
                    id: Date.now().toString(),
                    type: 'audio',
                    content: response.data.data.location,
                    key: response.data.data.key,
                };
                updateSequence([...(dataProduct?.sequence || []), newStep]);
            }
        } catch (error) {
            console.error('Erro ao salvar áudio:', error);
        } finally {
            setUploading(false);
        }
    };

    const cancelRecording = () => {
        if (timerRef.current) clearInterval(timerRef.current);
        recorder.stop(); // Stop/discard
        setIsRecording(false);
        setRecordingTime(0);
    };

    const updateStepContent = (id, newContent) => {
        const newSequence = (dataProduct?.sequence || []).map((step) =>
            step.id === id ? { ...step, content: newContent } : step
        );
        updateSequence(newSequence);
    };

    const removeStep = (id) => {
        const newSequence = (dataProduct?.sequence || []).filter((step) => step.id !== id);
        updateSequence(newSequence);
    };

    const formatTime = (seconds) => {
        const mins = Math.floor(seconds / 60);
        const secs = seconds % 60;
        return `${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
    };

    // Find current category value for Select, ensuring it exists in categories list
    // Handle both populated objects and string IDs
    const getCategoryId = () => {
        const cat = dataProduct?.category;
        if (!cat) return undefined;
        // If category is populated (object with _id)
        if (typeof cat === 'object' && cat._id) return cat._id;
        // If category is just an ID string
        return cat;
    };
    const currentCategory = getCategoryId();

    return (
        <Dialog open={open} onOpenChange={onClose}>
            <DialogContent className="max-w-[80vw] w-full h-[95vh] flex flex-col p-0 gap-0 rounded-2xl overflow-hidden bg-white border-none shadow-2xl">
                {/* Header */}
                <div className="p-6 border-b border-gray-100 bg-white sticky top-0 z-10">
                    <DialogHeader>
                        <DialogTitle className="text-xl font-bold text-gray-900 flex items-center gap-2">
                            <div className="p-2 bg-violet-100 rounded-lg">
                                <Iconify icon={isEdit ? 'solar:pen-bold' : 'solar:box-bold'} className="w-5 h-5 text-[#7C3AED]" />
                            </div>
                            {isEdit ? 'Editar Produto' : 'Novo Produto'}
                        </DialogTitle>
                    </DialogHeader>
                </div>

                {/* Tabs & Content */}
                <div className="flex-1 overflow-hidden flex flex-col bg-gray-100">
                    <Tabs value={activeTab} onValueChange={setActiveTab} className="h-full flex flex-col">
                        <div className="px-6 border-b border-gray-100 bg-white">
                            <TabsList className="bg-transparent gap-2 h-auto py-2">
                                <TabsTrigger
                                    value="info"
                                    className="data-[state=active]:bg-violet-50 data-[state=active]:text-violet-700 rounded-lg px-4 py-2"
                                >
                                    Informações
                                </TabsTrigger>
                                <TabsTrigger
                                    value="attributes"
                                    className="data-[state=active]:bg-violet-50 data-[state=active]:text-violet-700 rounded-lg px-4 py-2"
                                >
                                    Atributos e Tags
                                </TabsTrigger>
                                <TabsTrigger
                                    value="price"
                                    className="data-[state=active]:bg-violet-50 data-[state=active]:text-violet-700 rounded-lg px-4 py-2"
                                >
                                    Preço e Estoque
                                </TabsTrigger>
                                <TabsTrigger
                                    value="sequence"
                                    className="data-[state=active]:bg-violet-50 data-[state=active]:text-violet-700 rounded-lg px-4 py-2"
                                >
                                    Sequências de mensagem
                                </TabsTrigger>
                            </TabsList>
                        </div>

                        <div className="flex-1 overflow-y-auto p-6">
                            {/* Tab: Info */}
                            <TabsContent value="info" className="m-0 max-w-3xl mx-auto space-y-6 animate-in fade-in slide-in-from-bottom-2">
                                {/* Image Upload Component */}
                                <div className={CARD_CLASSES}>
                                    <h4 className="text-sm font-semibold text-gray-900 mb-4">Imagem de Capa</h4>
                                    <div className="flex justify-center">
                                        <div
                                            className="w-full aspect-video md:w-64 md:h-64 rounded-xl border-2 border-dashed border-gray-200 bg-gray-50 flex flex-col items-center justify-center cursor-pointer hover:border-violet-400 transition-colors relative overflow-hidden group"
                                            onClick={() => imageInputRef.current?.click()}
                                        >
                                            {dataProduct?.image ? (
                                                <>
                                                    <img src={dataProduct.image} alt="Produto" className="w-full h-full object-cover" />
                                                    <div className="absolute inset-0 bg-black/40 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity">
                                                        <Iconify icon="solar:camera-add-bold" className="w-8 h-8 text-white" />
                                                    </div>
                                                </>
                                            ) : (
                                                <>
                                                    {uploadingImage ? (
                                                        <CircularProgress size={24} className="text-violet-600" />
                                                    ) : (
                                                        <>
                                                            <div className="p-3 bg-white rounded-full mb-2 text-gray-400 group-hover:text-violet-500 transition-colors shadow-sm">
                                                                <Iconify icon="solar:camera-add-bold" className="w-6 h-6" />
                                                            </div>
                                                            <span className="text-xs font-medium text-gray-500">Adicionar Imagem</span>
                                                        </>
                                                    )}
                                                </>
                                            )}
                                            <input type="file" ref={imageInputRef} onChange={handleImageUpload} className="hidden" accept="image/*" />
                                        </div>
                                    </div>

                                    {/* Estado do produto para a busca por foto do agente.
                                        Aparece junto da imagem de propósito: é aqui que o
                                        lojista resolve, e "foto não serve" só sai trocando
                                        a imagem — esperar não adianta. */}
                                    {isEdit && (() => {
                                        const estados = {
                                            pronto: {
                                                texto: 'O agente de IA encontra este produto quando o cliente manda uma foto parecida.',
                                                classe: 'bg-emerald-50 border-emerald-200 text-emerald-800',
                                                icone: 'eva:checkmark-circle-2-outline',
                                            },
                                            pendente: {
                                                texto: 'A foto ainda vai ser preparada para a busca por imagem. Acontece em segundo plano.',
                                                classe: 'bg-gray-50 border-gray-200 text-gray-600',
                                                icone: 'eva:clock-outline',
                                            },
                                            descartada: {
                                                texto: 'Esta imagem parece um logotipo, banner ou foto genérica — imagem assim se parece com tudo e atrapalharia o resultado, então ficou de fora da busca por foto. Troque por uma foto do produto em si.',
                                                classe: 'bg-amber-50 border-amber-200 text-amber-800',
                                                icone: 'eva:alert-triangle-outline',
                                            },
                                            sem_foto: {
                                                texto: 'Sem imagem cadastrada, o agente de IA não consegue achar este produto quando o cliente manda uma foto.',
                                                classe: 'bg-gray-50 border-gray-200 text-gray-600',
                                                icone: 'eva:image-outline',
                                            },
                                        };
                                        const st = estados[dataProduct?.statusBuscaImagem] || estados.sem_foto;
                                        return (
                                            <div className={`mt-4 flex items-start gap-2 rounded-xl border px-3 py-2.5 ${st.classe}`}>
                                                <Iconify icon={st.icone} width={16} className="mt-[1px] shrink-0" />
                                                <p className="text-xs leading-relaxed">{st.texto}</p>
                                            </div>
                                        );
                                    })()}
                                </div>

                                <div className={CARD_CLASSES}>
                                    <h4 className="text-sm font-semibold text-gray-900 mb-4">Detalhes Básicos</h4>
                                    <div className="grid gap-6">
                                        <div>
                                            <label className={LABEL_CLASSES}>Nome do Produto</label>
                                            <Input
                                                value={dataProduct?.name || ''}
                                                onChange={changeName}
                                                placeholder="Ex: Consultoria Premium"
                                                className={INPUT_CLASSES}
                                            />
                                        </div>
                                        <div className="grid grid-cols-2 gap-4">
                                            <div>
                                                <label className={LABEL_CLASSES}>SKU / Código</label>
                                                <Input
                                                    value={dataProduct?.sku || ''}
                                                    onChange={changeSku}
                                                    placeholder="Ex: PROD-001"
                                                    className={INPUT_CLASSES}
                                                />
                                            </div>
                                            <div>
                                                <label className={LABEL_CLASSES}>Referência (Ref)</label>
                                                <Input
                                                    value={dataProduct?.ref || ''}
                                                    onChange={changeRef}
                                                    placeholder="Ex: REF-123"
                                                    className={INPUT_CLASSES}
                                                />
                                            </div>
                                        </div>
                                        <div>
                                            <label className={LABEL_CLASSES}>Descrição</label>
                                            <Textarea
                                                value={dataProduct?.description || ''}
                                                onChange={changeDescription}
                                                placeholder="Detalhes do produto..."
                                                rows={5}
                                                className={INPUT_CLASSES}
                                            />
                                        </div>
                                        <div>
                                            <label className={LABEL_CLASSES}>Link do Produto</label>
                                            <Input
                                                type="url"
                                                value={dataProduct?.link || ''}
                                                onChange={changeLink}
                                                placeholder="https://exemplo.com/produto"
                                                className={INPUT_CLASSES}
                                            />
                                        </div>
                                        <div>
                                            <label className={LABEL_CLASSES}>Categoria</label>
                                            <div className="flex gap-2">
                                                <Select value={currentCategory} onValueChange={changeCategory}>
                                                    <SelectTrigger className={INPUT_CLASSES}>
                                                        <SelectValue placeholder="Selecione..." />
                                                    </SelectTrigger>
                                                    <SelectContent>
                                                        {categories.map((cat) => (
                                                            <SelectItem key={cat._id || cat.id} value={cat._id || cat.id}>
                                                                {cat.name}
                                                            </SelectItem>
                                                        ))}
                                                    </SelectContent>
                                                </Select>
                                                <Button
                                                    size="icon"
                                                    variant="outline"
                                                    className="shrink-0 border-dashed border-gray-300 hover:border-violet-500 hover:text-violet-600"
                                                    onClick={() => setOpenCategories(true)}
                                                >
                                                    <Iconify icon="solar:settings-bold" className="w-4 h-4" />
                                                </Button>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </TabsContent>

                            {/* Tab: Attributes & Tags */}
                            <TabsContent
                                value="attributes"
                                className="m-0 max-w-3xl mx-auto space-y-6 animate-in fade-in slide-in-from-bottom-2"
                            >
                                <div className={CARD_CLASSES}>
                                    <label className={LABEL_CLASSES}>Etiquetas (Tags)</label>
                                    <div className="flex gap-2 mb-4">
                                        <Input
                                            value={tagInput}
                                            onChange={(e) => setTagInput(e.target.value)}
                                            onKeyDown={(e) => e.key === 'Enter' && handleAddTag()}
                                            placeholder="Adicionar tag..."
                                            className={INPUT_CLASSES}
                                        />
                                        <Button size="icon" onClick={handleAddTag} className="shrink-0 bg-violet-100 hover:bg-violet-200 text-violet-700">
                                            <Iconify icon="solar:add-circle-bold" className="w-5 h-5" />
                                        </Button>
                                    </div>
                                    <div className="flex flex-wrap gap-2 p-4 bg-gray-50 rounded-xl min-h-[60px] border border-dashed border-gray-200">
                                        {dataProduct?.tags?.length > 0 ? (
                                            dataProduct.tags.map((tag, index) => (
                                                <Badge
                                                    key={index}
                                                    variant="secondary"
                                                    className="bg-white text-gray-700 hover:bg-red-50 hover:text-red-600 cursor-pointer transition-colors shadow-sm border border-gray-100"
                                                    onClick={() => handleRemoveTag(tag)}
                                                >
                                                    {tag}
                                                    <Iconify icon="solar:close-circle-bold" className="w-3 h-3 ml-1.5" />
                                                </Badge>
                                            ))
                                        ) : (
                                            <span className="text-sm text-gray-400 italic">Nenhuma tag adicionada.</span>
                                        )}
                                    </div>
                                </div>

                                <div>
                                    <div className="flex items-center justify-between mb-4">
                                        <label className={LABEL_CLASSES.replace('mb-2', 'mb-0')}>Atributos do Produto</label>
                                        <Button
                                            size="sm"
                                            className="bg-violet-100 hover:bg-violet-200 text-violet-700 border-none shadow-none"
                                            onClick={() => {
                                                const current = dataProduct?.attributes || [];
                                                changeAttributes([...current, { name: '', values: '' }]);
                                            }}
                                        >
                                            <Iconify icon="solar:add-circle-bold" className="w-4 h-4 mr-2" />
                                            Adicionar Atributo
                                        </Button>
                                    </div>
                                    <div className="grid gap-4">
                                        {dataProduct?.attributes?.map((attr, attrIndex) => (
                                            <div
                                                key={attrIndex}
                                                className="group flex items-center gap-3 p-2 bg-white rounded-lg border border-gray-200 shadow-sm hover:border-violet-300 transition-colors"
                                            >
                                                <div className="w-[140px] shrink-0">
                                                    <Input
                                                        placeholder="Nome (Ex: Cor)"
                                                        value={attr.name}
                                                        onChange={(e) => {
                                                            const newAttrs = [...(dataProduct?.attributes || [])];
                                                            newAttrs[attrIndex].name = e.target.value;
                                                            changeAttributes(newAttrs);
                                                        }}
                                                        className="h-8 text-sm bg-gray-50 border-transparent focus:bg-white transition-all"
                                                    />
                                                </div>
                                                <div className="w-px h-6 bg-gray-200 mx-1" />
                                                <div className="flex-1">
                                                    <Input
                                                        placeholder="Valor (Ex: Azul)"
                                                        value={attr.values || ''}
                                                        onChange={(e) => {
                                                            const newAttrs = [...(dataProduct?.attributes || [])];
                                                            newAttrs[attrIndex].values = e.target.value;
                                                            changeAttributes(newAttrs);
                                                        }}
                                                        className="h-8 text-sm shadow-none border-transparent focus:underline px-2"
                                                    />
                                                </div>
                                                <Button
                                                    size="icon"
                                                    variant="ghost"
                                                    className="h-7 w-7 opacity-0 group-hover:opacity-100 transition-opacity text-gray-400 hover:text-red-500 hover:bg-red-50"
                                                    onClick={() => {
                                                        const newAttrs = (dataProduct?.attributes || []).filter((_, i) => i !== attrIndex);
                                                        changeAttributes(newAttrs);
                                                    }}
                                                >
                                                    <Iconify icon="solar:trash-bin-trash-bold" className="w-4 h-4" />
                                                </Button>
                                            </div>
                                        ))}
                                        {(!dataProduct?.attributes || dataProduct.attributes.length === 0) && (
                                            <div className="flex flex-col items-center justify-center py-8 border-2 border-dashed border-gray-300 rounded-xl bg-white">
                                                <Iconify icon="solar:hamburger-menu-linear" className="w-8 h-8 text-gray-300 mb-2" />
                                                <p className="text-sm text-gray-500 font-medium">Nenhum atributo definido</p>
                                                <p className="text-xs text-gray-400">Adicione variações como tamanho, cor, material...</p>
                                            </div>
                                        )}
                                    </div>
                                </div>
                            </TabsContent>

                            {/* Tab: Price & Stock */}
                            <TabsContent value="price" className="m-0 max-w-3xl mx-auto space-y-6 animate-in fade-in slide-in-from-bottom-2">
                                <div className={CARD_CLASSES}>
                                    <h4 className="text-sm font-semibold text-gray-900 mb-4 flex items-center gap-2">
                                        <Iconify icon="solar:tag-price-bold" className="text-violet-500" />
                                        Precificação
                                    </h4>
                                    <div className="grid grid-cols-2 gap-6">
                                        <div>
                                            <label className={LABEL_CLASSES}>Preço Original (R$)</label>
                                            <div className="relative">
                                                <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 font-medium">R$</span>
                                                <Input
                                                    type="number"
                                                    value={dataProduct?.price || ''}
                                                    onChange={changePrice}
                                                    className={`${INPUT_CLASSES} pl-9`}
                                                    placeholder="0,00"
                                                />
                                            </div>
                                        </div>
                                        <div>
                                            <label className={LABEL_CLASSES}>Preço Promocional (R$)</label>
                                            <div className="relative">
                                                <span className="absolute left-3 top-1/2 -translate-y-1/2 text-green-500 font-medium">R$</span>
                                                <Input
                                                    type="number"
                                                    value={dataProduct?.priceSale || ''}
                                                    onChange={changePriceSale}
                                                    className={`${INPUT_CLASSES} pl-9 border-green-100 focus:border-green-500`}
                                                    placeholder="0,00"
                                                />
                                            </div>
                                        </div>
                                    </div>
                                </div>

                                <div className={CARD_CLASSES}>
                                    <h4 className="text-sm font-semibold text-gray-900 mb-4 flex items-center gap-2">
                                        <Iconify icon="solar:box-bold" className="text-blue-500" />
                                        Controle de Estoque
                                    </h4>
                                    <div className="flex items-end gap-6">
                                        <div className="flex-1">
                                            <label className={LABEL_CLASSES}>Quantidade em Estoque</label>
                                            <Input
                                                type="number"
                                                value={dataProduct?.stock || ''}
                                                onChange={changeStock}
                                                className={INPUT_CLASSES}
                                                placeholder="0"
                                            />
                                        </div>
                                        <div className="flex-1">
                                            <div className="flex items-center justify-between p-3 bg-gray-50 rounded-xl border border-gray-200 h-[44px]">
                                                <span className="text-sm font-medium text-gray-700">Produto em Promoção</span>
                                                <Switch checked={dataProduct?.sale || false} onCheckedChange={changeSale} />
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </TabsContent>

                            {/* Tab: Sequence */}
                            <TabsContent value="sequence" className="m-0 h-full flex flex-col animate-in fade-in slide-in-from-bottom-2">
                                <div className="flex items-center justify-between mb-6">
                                    <div>
                                        <h3 className={SECTION_TITLE}>Sequências</h3>
                                        <p className="text-sm text-gray-500">Conteúdos entregues automaticamente após a compra.</p>
                                    </div>
                                    {!isRecording && (
                                        <div className="flex gap-2">
                                            <Button
                                                variant="outline"
                                                size="sm"
                                                onClick={addTextStep}
                                                className="gap-2 border-dashed border-gray-300 hover:border-violet-500 hover:text-violet-600 bg-white"
                                            >
                                                <Iconify icon="solar:text-field-bold" />
                                                Texto
                                            </Button>
                                            <Button
                                                variant="outline"
                                                size="sm"
                                                onClick={() => fileInputRef.current?.click()}
                                                className="gap-2 border-dashed border-gray-300 hover:border-violet-500 hover:text-violet-600 bg-white"
                                                disabled={uploading}
                                            >
                                                <Iconify icon="solar:file-bold" />
                                                Arquivo
                                            </Button>
                                            <Button
                                                variant="outline"
                                                size="sm"
                                                onClick={startRecording}
                                                className="gap-2 border-dashed border-gray-300 hover:border-violet-500 hover:text-violet-600 bg-white"
                                            >
                                                <Iconify icon="solar:microphone-bold" />
                                                Voz
                                            </Button>
                                        </div>
                                    )}
                                    <input type="file" ref={fileInputRef} onChange={handleFileUpload} className="hidden" multiple />
                                </div>

                                {/* Recording UI overlay */}
                                {isRecording && (
                                    <div className="mb-6 p-4 rounded-xl bg-red-50 border border-red-200 flex items-center justify-between animate-in fade-in slide-in-from-top-2">
                                        <div className="flex items-center gap-3">
                                            <div className="w-3 h-3 rounded-full bg-red-500 animate-pulse" />
                                            <span className="font-mono font-medium text-red-700">Gravando: {formatTime(recordingTime)}</span>
                                        </div>
                                        <div className="flex gap-2">
                                            <Button size="sm" variant="ghost" onClick={cancelRecording} className="text-red-600 hover:bg-red-100">
                                                Cancelar
                                            </Button>
                                            <Button size="sm" variant="destructive" onClick={stopRecording}>
                                                Concluir
                                            </Button>
                                        </div>
                                    </div>
                                )}

                                {uploading && (
                                    <div className="mb-6 flex items-center gap-3 p-4 rounded-xl bg-violet-50 border border-violet-100">
                                        <CircularProgress size={20} className="text-violet-600" />
                                        <span className="text-sm font-medium text-violet-700">Processando upload...</span>
                                    </div>
                                )}

                                {/* Steps List */}
                                <div className="flex flex-col gap-4 flex-1 pb-4">
                                    {(!dataProduct?.sequence || dataProduct.sequence.length === 0) && !isRecording && (
                                        <div className="flex flex-col items-center justify-center py-12 border-2 border-dashed border-gray-300 rounded-xl bg-white">
                                            <div className="w-12 h-12 rounded-full bg-gray-50 flex items-center justify-center mb-3 text-gray-400">
                                                <Iconify icon="solar:layers-minimalistic-line-duotone" className="w-6 h-6" />
                                            </div>
                                            <p className="text-gray-500 font-medium">Nenhuma etapa cadastrada</p>
                                            <p className="text-xs text-gray-400">Adicione mensagens, arquivos ou áudios.</p>
                                        </div>
                                    )}

                                    {dataProduct?.sequence?.map((step, index) => (
                                        <div key={step.id} className="group relative flex gap-4 animate-in fade-in slide-in-from-bottom-2 duration-300">
                                            {/* Number/Icon */}
                                            <div className="flex flex-col items-center gap-2 pt-2">
                                                <div className="w-8 h-8 rounded-full bg-[#7C3AED] text-white flex items-center justify-center text-xs font-bold shadow-md shadow-violet-200 z-10">
                                                    {index + 1}
                                                </div>
                                                {index !== dataProduct.sequence.length - 1 && <div className="w-0.5 flex-1 bg-gray-300" />}
                                            </div>

                                            {/* Content Card */}
                                            <div className="flex-1 min-w-0">
                                                <div className="p-4 rounded-xl border border-gray-200 bg-white hover:shadow-md transition-all">
                                                    <div className="flex justify-between items-start mb-2">
                                                        <div className="flex items-center gap-2">
                                                            <Badge variant="secondary" className="bg-gray-100 text-gray-600 capitalize px-2 py-0.5 text-[10px]">
                                                                {step.type === 'text' && (
                                                                    <>
                                                                        <Iconify icon="solar:text-field-bold" className="mr-1 w-3 h-3" /> Mensagem
                                                                    </>
                                                                )}
                                                                {step.type === 'file' && (
                                                                    <>
                                                                        <Iconify icon="solar:file-bold" className="mr-1 w-3 h-3" /> Arquivo
                                                                    </>
                                                                )}
                                                                {step.type === 'audio' && (
                                                                    <>
                                                                        <Iconify icon="solar:microphone-bold" className="mr-1 w-3 h-3" /> Áudio
                                                                    </>
                                                                )}
                                                            </Badge>
                                                        </div>
                                                        <Button
                                                            variant="ghost"
                                                            size="icon"
                                                            className="h-6 w-6 text-gray-400 hover:text-red-500 -mr-2 -mt-2"
                                                            onClick={() => removeStep(step.id)}
                                                        >
                                                            <Iconify icon="solar:trash-bin-trash-bold" className="w-4 h-4" />
                                                        </Button>
                                                    </div>

                                                    {/* Step Content Render */}
                                                    {step.type === 'text' && (
                                                        <Textarea
                                                            value={step.content}
                                                            onChange={(e) => updateStepContent(step.id, e.target.value)}
                                                            placeholder="Digite o conteúdo da mensagem..."
                                                            className="min-h-[80px] text-sm bg-transparent border-gray-200 resize-none focus:ring-0 focus:border-violet-500"
                                                        />
                                                    )}

                                                    {step.type === 'file' && (
                                                        <div className="flex items-center gap-3 p-3 rounded-lg bg-gray-50 border border-gray-100">
                                                            <div className="p-2 bg-blue-100 rounded text-blue-600">
                                                                <Iconify icon="solar:file-bold" className="w-5 h-5" />
                                                            </div>
                                                            <div className="flex-1 min-w-0">
                                                                <p className="text-sm font-medium text-gray-900 truncate">{step.name || 'Arquivo sem nome'}</p>
                                                                <a
                                                                    href={step.content}
                                                                    target="_blank"
                                                                    rel="noopener noreferrer"
                                                                    className="text-xs text-violet-600 hover:underline truncate block"
                                                                >
                                                                    Visualizar Arquivo
                                                                </a>
                                                            </div>
                                                        </div>
                                                    )}

                                                    {step.type === 'audio' && (
                                                        <div className="flex items-center gap-3 p-3 rounded-lg bg-gray-50 border border-gray-100">
                                                            <div className="p-2 bg-purple-100 rounded text-purple-600">
                                                                <Iconify icon="solar:play-circle-bold" className="w-5 h-5" />
                                                            </div>
                                                            <audio controls src={step.content} className="h-8 w-full max-w-[250px]" />
                                                        </div>
                                                    )}
                                                </div>
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            </TabsContent>
                        </div>
                    </Tabs>
                </div>

                {/* Footer */}
                <div className="p-6 border-t border-gray-100 bg-white sticky bottom-0 z-10 flex justify-end gap-3">
                    <Button variant="outline" onClick={onClose} className="rounded-xl border-gray-200 text-gray-600 hover:bg-gray-50">
                        Cancelar
                    </Button>
                    <Button
                        onClick={!isEdit ? onSubmit : onEdit}
                        className="rounded-xl bg-[#7C3AED] hover:bg-[#6d28d9] text-white shadow-lg shadow-violet-500/20"
                    >
                        {!isEdit ? 'Criar Produto' : 'Salvar Alterações'}
                    </Button>
                </div>

                <ModalCategorias
                    open={openCategories}
                    onClose={() => setOpenCategories(false)}
                    categories={categories}
                    categoryActions={categoryActions}
                />
            </DialogContent>
        </Dialog>
    );
}